import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import {
  Button,
  Card,
  FAB,
  Modal,
  Portal,
  Switch,
  TextInput,
  Snackbar,
  Dialog,
  Paragraph,
} from "react-native-paper";
import { MaterialIcons } from "@expo/vector-icons";
import {
  configService,
  ConfigurationDocument,
} from "../../services/configService";
import { runMigration } from "../../scripts/migrations/migrateConfigsToFirestore";
import { Config } from "../../config/companies/config";

interface ConfigManagementScreenProps {
  navigation: any;
}

export default function ConfigManagementScreen({
  navigation,
}: ConfigManagementScreenProps) {
  const [configurations, setConfigurations] = useState<ConfigurationDocument[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] =
    useState<ConfigurationDocument | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    key: "",
    description: "",
    isActive: true,
    mainCollection: "",
  });
  const [snackbar, setSnackbar] = useState({ visible: false, message: "" });
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    content: "",
    onConfirm: () => {},
  });
  const [collectionDropdownVisible, setCollectionDropdownVisible] =
    useState(false);
  const [availableCollections, setAvailableCollections] = useState<string[]>(
    [],
  );
  const [currentCollections, setCurrentCollections] = useState<string[]>([]);

  useEffect(() => {
    loadConfigurations();
  }, []);

  useEffect(() => {
    // Extract unique collection names from all configs
    const collectionsSet = new Set<string>();
    configurations.forEach((config) => {
      const dbCollections = config.config?.database?.collections || [];
      dbCollections.forEach((col: any) => {
        if (col.name) collectionsSet.add(col.name);
      });
    });
    setAvailableCollections(Array.from(collectionsSet));
  }, [configurations]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await configService.getAllConfigurations();
      setConfigurations(configs);
    } catch (error) {
      console.error("Error loading configurations:", error);
      setSnackbar({ visible: true, message: "Failed to load configurations" });
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadConfigurations();
    setRefreshing(false);
  };

  const handleEditConfig = (config: ConfigurationDocument) => {
    setEditingConfig(config);
    // Extract collections from the config being edited
    const dbCollections = config.config?.database?.collections || [];
    setCurrentCollections(
      dbCollections.map((col: any) => col.name).filter(Boolean),
    );
    setFormData({
      name: config.name,
      key: config.key,
      description: config.description || "",
      isActive: config.isActive,
      mainCollection: config.config?.mainCollection || "",
    });
    setModalVisible(true);
  };

  const handleCreateConfig = () => {
    setEditingConfig(null);
    // Use a default structure for new configs (could be empty or a default list)
    const defaultCollections = [`${formData.key || "default"}-collection`];
    setCurrentCollections(defaultCollections);
    setFormData({
      name: "",
      key: "",
      description: "",
      isActive: true,
      mainCollection: defaultCollections[0] || "",
    });
    setModalVisible(true);
  };

  const handleSaveConfig = async () => {
    try {
      if (!formData.name || !formData.key) {
        setSnackbar({ visible: true, message: "Name and key are required" });
        return;
      }
      if (!formData.mainCollection) {
        setSnackbar({ visible: true, message: "Please select a collection" });
        return;
      }

      if (editingConfig) {
        // Update existing configuration
        await configService.updateConfiguration(editingConfig.id, {
          name: formData.name,
          key: formData.key,
          description: formData.description,
          isActive: formData.isActive,
          // Optionally update mainCollection in config
          config: {
            ...editingConfig.config,
            mainCollection: formData.mainCollection,
          },
        });
        setSnackbar({
          visible: true,
          message: "Configuration updated successfully",
        });
      } else {
        // Create new configuration with default config structure
        const defaultConfig: Config = {
          appName: formData.name,
          mainCollection: formData.mainCollection,
          theme: {
            primaryColor: "#3498db",
            secondaryColor: "#2ecc71",
          },
          roles: ["admin", "user"],
          defaultRole: "user",
          statuses: ["pending", "in progress", "completed"],
          employeesVisibleTo: ["admin"],
          employeesEditableBy: ["admin"],
          screens: [
            {
              name: "MainScreen",
              type: "board",
              collection: formData.mainCollection,
              roles: ["admin", "user"],
              defaultPresentation: "horizontal",
              where: [
                {
                  field: "archived",
                  operator: "==",
                  value: false,
                },
              ],
            },
          ],
          database: {
            collections: [
              {
                name: formData.mainCollection,
                deletableBy: ["admin"],
                fields: [
                  {
                    name: "title",
                    type: "text",
                    label: "Title",
                    list: ["admin", "user"],
                    formCreate: ["admin", "user"],
                    formEdit: ["admin", "user"],
                    board: ["admin", "user"],
                    required: true,
                    autoFocus: true,
                  },
                  {
                    name: "description",
                    type: "textarea",
                    label: "Description",
                    list: ["admin", "user"],
                    formCreate: ["admin", "user"],
                    formEdit: ["admin", "user"],
                    board: ["admin", "user"],
                  },
                  {
                    name: "status",
                    type: "status",
                    label: "Status",
                    defaultValue: "pending",
                    list: ["admin", "user"],
                    formEdit: ["admin", "user"],
                    board: ["admin", "user"],
                  },
                  {
                    name: "createdAt",
                    type: "dateTime",
                    label: "Created At",
                    list: ["admin", "user"],
                    form: [],
                    board: ["admin", "user"],
                  },
                ],
              },
            ],
          },
        };

        await configService.createConfiguration({
          name: formData.name,
          key: formData.key,
          description: formData.description,
          config: defaultConfig,
          isActive: formData.isActive,
        });
        setSnackbar({
          visible: true,
          message: "Configuration created successfully",
        });
      }

      setModalVisible(false);
      loadConfigurations();
    } catch (error) {
      console.error("Error saving configuration:", error);
      setSnackbar({ visible: true, message: "Failed to save configuration" });
    }
  };

  const handleDeleteConfig = (config: ConfigurationDocument) => {
    setDialog({
      visible: true,
      title: "Delete Configuration",
      content: `Are you sure you want to delete "${config.name}"?`,
      onConfirm: async () => {
        try {
          await configService.deleteConfiguration(config.id);
          setSnackbar({
            visible: true,
            message: "Configuration deleted successfully",
          });
          loadConfigurations();
        } catch (error) {
          console.error("Error deleting configuration:", error);
          setSnackbar({
            visible: true,
            message: "Failed to delete configuration",
          });
        }
      },
    });
  };

  const handleToggleStatus = async (config: ConfigurationDocument) => {
    try {
      await configService.toggleConfigurationStatus(
        config.id,
        !config.isActive,
      );
      loadConfigurations();
    } catch (error) {
      console.error("Error toggling configuration status:", error);
      setSnackbar({
        visible: true,
        message: "Failed to toggle configuration status",
      });
    }
  };

  const handleMigrateConfigs = async () => {
    setDialog({
      visible: true,
      title: "Migrate Configurations",
      content:
        "This will migrate all existing hardcoded configurations to Firestore. Continue?",
      onConfirm: async () => {
        try {
          const result = await runMigration();
          if (result.success) {
            setSnackbar({ visible: true, message: result.message });
            loadConfigurations();
          } else {
            setSnackbar({ visible: true, message: result.message });
          }
        } catch (error) {
          console.error("Migration error:", error);
          setSnackbar({ visible: true, message: "Migration failed" });
        }
      },
    });
  };

  const renderConfigCard = (config: ConfigurationDocument) => (
    <Card key={config.id} style={styles.card}>
      <Card.Content>
        <View style={styles.cardHeader}>
          <View style={styles.cardTitleContainer}>
            <Text style={styles.cardTitle}>{config.name}</Text>
            <Text style={styles.cardKey}>Key: {config.key}</Text>
          </View>
          <Switch
            value={config.isActive}
            onValueChange={() => handleToggleStatus(config)}
          />
        </View>

        {config.description && (
          <Text style={styles.cardDescription}>{config.description}</Text>
        )}

        <View style={styles.cardMeta}>
          <Text style={styles.cardMetaText}>
            Created: {config.createdAt.toLocaleDateString()}
          </Text>
          <Text style={styles.cardMetaText}>
            Updated: {config.updatedAt.toLocaleDateString()}
          </Text>
        </View>
      </Card.Content>

      <Card.Actions>
        <Button
          mode="outlined"
          onPress={() =>
            navigation.navigate("ConfigEditor", { configId: config.id })
          }
          icon="cog"
        >
          Edit Config
        </Button>
        <Button
          mode="outlined"
          onPress={() => handleEditConfig(config)}
          icon="pencil"
        >
          Edit Meta
        </Button>
        <Button
          mode="outlined"
          onPress={() => handleDeleteConfig(config)}
          icon="delete"
          textColor="red"
        >
          Delete
        </Button>
      </Card.Actions>
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
        <Text>Loading configurations...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.header}>
          <Text style={styles.title}>Configuration Management</Text>
          <Text style={styles.subtitle}>Manage your app configurations</Text>
          <Button
            mode="outlined"
            onPress={handleMigrateConfigs}
            icon="database-import"
            style={styles.migrateButton}
          >
            Migrate Existing Configs
          </Button>
        </View>

        {configurations.length === 0 ? (
          <View style={styles.emptyContainer}>
            <MaterialIcons name="settings" size={64} color="#ccc" />
            <Text style={styles.emptyText}>No configurations found</Text>
            <Text style={styles.emptySubtext}>
              Create your first configuration to get started
            </Text>
          </View>
        ) : (
          <View style={styles.configList}>
            {configurations.map(renderConfigCard)}
          </View>
        )}
      </ScrollView>

      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={styles.modal}
        >
          <KeyboardAwareScrollView
            bottomOffset={20}
            keyboardShouldPersistTaps="handled"
          >
            <Text style={styles.modalTitle}>
              {editingConfig ? "Edit Configuration" : "Create Configuration"}
            </Text>

            <TextInput
              label="Name"
              value={formData.name}
              onChangeText={(text) => setFormData({ ...formData, name: text })}
              style={styles.input}
              mode="outlined"
            />

            <TextInput
              label="Key (unique identifier)"
              value={formData.key}
              onChangeText={(text) => setFormData({ ...formData, key: text })}
              style={styles.input}
              mode="outlined"
              disabled={!!editingConfig} // Don't allow editing key for existing configs
            />

            <TextInput
              label="Description (optional)"
              value={formData.description}
              onChangeText={(text) =>
                setFormData({ ...formData, description: text })
              }
              style={styles.input}
              mode="outlined"
              multiline
              numberOfLines={3}
            />

            {/* Collection Dropdown */}
            <View style={styles.input}>
              <Text style={{ marginBottom: 4 }}>Main Collection</Text>
              <Button
                mode="outlined"
                onPress={() => setCollectionDropdownVisible(true)}
                style={{ justifyContent: "flex-start" }}
                contentStyle={{ justifyContent: "flex-start" }}
              >
                {formData.mainCollection || "Select a collection"}
              </Button>
              <Portal>
                <Dialog
                  visible={collectionDropdownVisible}
                  onDismiss={() => setCollectionDropdownVisible(false)}
                >
                  <Dialog.Title>Select Collection</Dialog.Title>
                  <Dialog.Content>
                    {currentCollections.length === 0 ? (
                      <Text>No collections found in this config</Text>
                    ) : (
                      currentCollections.map((col) => (
                        <Button
                          key={col}
                          onPress={() => {
                            setFormData({ ...formData, mainCollection: col });
                            setCollectionDropdownVisible(false);
                          }}
                          style={{ marginVertical: 2 }}
                        >
                          {col}
                        </Button>
                      ))
                    )}
                  </Dialog.Content>
                  <Dialog.Actions>
                    <Button onPress={() => setCollectionDropdownVisible(false)}>
                      Cancel
                    </Button>
                  </Dialog.Actions>
                </Dialog>
              </Portal>
            </View>

            <View style={styles.switchContainer}>
              <Text>Active</Text>
              <Switch
                value={formData.isActive}
                onValueChange={(value) =>
                  setFormData({ ...formData, isActive: value })
                }
              />
            </View>

            <View style={styles.modalActions}>
              <Button
                mode="outlined"
                onPress={() => setModalVisible(false)}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={handleSaveConfig}
                style={styles.modalButton}
              >
                {editingConfig ? "Update" : "Create"}
              </Button>
            </View>
          </KeyboardAwareScrollView>
        </Modal>
        <Dialog
          visible={dialog.visible}
          onDismiss={() => setDialog({ ...dialog, visible: false })}
        >
          <Dialog.Title>{dialog.title}</Dialog.Title>
          <Dialog.Content>
            <Paragraph>{dialog.content}</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialog({ ...dialog, visible: false })}>
              Cancel
            </Button>
            <Button
              onPress={async () => {
                setDialog({ ...dialog, visible: false });
                await dialog.onConfirm();
              }}
            >
              OK
            </Button>
          </Dialog.Actions>
        </Dialog>
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar({ ...snackbar, visible: false })}
          duration={3000}
        >
          {snackbar.message}
        </Snackbar>
      </Portal>

      <FAB icon="plus" style={styles.fab} onPress={handleCreateConfig} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: "#666",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 16,
    color: "#666",
  },
  emptySubtext: {
    fontSize: 14,
    color: "#999",
    textAlign: "center",
    marginTop: 8,
  },
  configList: {
    padding: 16,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  cardTitleContainer: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 4,
  },
  cardKey: {
    fontSize: 12,
    color: "#666",
    fontFamily: "monospace",
  },
  cardDescription: {
    fontSize: 14,
    color: "#666",
    marginBottom: 8,
  },
  cardMeta: {
    marginTop: 8,
  },
  cardMetaText: {
    fontSize: 12,
    color: "#999",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    bottom: 0,
  },
  modal: {
    backgroundColor: "white",
    margin: 20,
    padding: 20,
    borderRadius: 8,
    maxHeight: "80%",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    textAlign: "center",
  },
  input: {
    marginBottom: 16,
  },
  switchContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  modalButton: {
    flex: 1,
    marginHorizontal: 8,
  },
  migrateButton: {
    marginTop: 12,
  },
});
